import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import {
  LocationVarInterface,
  LocationVarKey,
  LocationVarWithId,
  LocationVarsById,
  LocationVarsWithIdByKey,
  LocationVarsWithIdByKeyByGroupOrLocationId,
} from "@swivl-mono/types";
import { create } from "zustand";

import LocationsModel from "../Locations/Locations.Model";
import { Member } from "../Member/Member.Current";
import { AlertsModel } from "../Notifications/Alerts.Model";

interface LocationVarsModelStoreInterface {
  allVariables: LocationVarsById;
  orgVariables: LocationVarsWithIdByKey;

  groupVariables: LocationVarsWithIdByKeyByGroupOrLocationId;

  locationVariables: LocationVarsWithIdByKeyByGroupOrLocationId;
  isLoaded: boolean;
  isLoading: boolean;
}

const BlankState: LocationVarsModelStoreInterface = {
  allVariables: {},
  orgVariables: {},
  groupVariables: {},
  locationVariables: {},
  isLoaded: false,
  isLoading: false,
};

export default class LocationVarsModel {
  static useState = create<LocationVarsModelStoreInterface>((set) => ({
    ...BlankState,
  }));
  static setState = LocationVarsModel.useState.setState;
  static get state() {
    return LocationVarsModel.useState.getState();
  }

  static async loadAllVariables(force?: boolean) {
    if (LocationVarsModel.state.isLoaded && !force) {
      return;
    }
    if (LocationVarsModel.state.isLoading) {
      return;
    }
    if (!Member.orgId) {
      return;
    }
    LocationVarsModel.setState({
      isLoading: true,
    });

    try {
      const variables = await API.LocationVars.loadVariables(Member.orgId!);

      LocationVarsModel.updateAllVariables(variables as LocationVarInterface[]);
      return true;
    } catch (error) {
      AlertsModel.errorToast("Error loading location variables");
      warn("&&& Error loading location variables", error);
      return false;
    }
  }
  static updateAllVariables(variables?: LocationVarInterface[]) {
    if (!variables) {
      return;
    }
    let state = LocationVarsModel.state;
    state.isLoading = false;
    state.isLoaded = true;
    state.allVariables = {};
    state.orgVariables = {};
    state.groupVariables = {};
    state.locationVariables = {};
    variables.forEach((variable) => {
      state.allVariables![variable.id] = variable;
      if (variable.locationId) {
        if (!state.locationVariables![variable.locationId]) {
          state.locationVariables![variable.locationId] = {};
        }

        state.locationVariables![variable.locationId]![variable.key as LocationVarKey] = {
          id: variable.id,
          value: variable.value,
          key: variable.key,
        };
      } else if (variable.groupId) {
        if (!state.groupVariables![variable.groupId]) {
          state.groupVariables![variable.groupId] = {};
        }
        state.groupVariables![variable.groupId]![variable.key as LocationVarKey] = {
          id: variable.id,
          value: variable.value,
          key: variable.key,
        };
      } else {
        state.orgVariables![variable.key as LocationVarKey] = {
          id: variable.id,
          value: variable.value,
          key: variable.key,
        };
      }
    });
    LocationsModel.updateLocationsWithVariables(state.orgVariables, state.groupVariables, state.locationVariables);
    LocationVarsModel.setState({ ...state });
  }

  static async createVariable(key: LocationVarKey, value: string, groupId?: number, locationId?: number) {
    const newVar = await API.LocationVars.createVariable(key, value, Member.orgId!, groupId, locationId);

    const allVariables = LocationVarsModel.state.allVariables;
    LocationVarsModel.updateAllVariables([...Object.values(allVariables as LocationVarInterface[]), newVar]);
    return;
  }

  static async saveVariable(variableId: number, value?: string) {
    try {
      let allVariables = LocationVarsModel.state.allVariables;

      if (!allVariables || !allVariables[variableId]) {
        AlertsModel.errorToast("Error editing variable - Not Found");
        return;
      }

      allVariables[variableId]!.value = value || "";
      LocationVarsModel.updateAllVariables(Object.values(allVariables as LocationVarInterface[]));
      if (typeof value !== "undefined" && value !== null && typeof value !== "string") {
        value = String(value);
      }
      const v = await API.LocationVars.updateVariable(Member.orgId!, variableId, value);
      // LocationVarsModel.postProcessVariableIfNeeded(v);

      // AlertsModel.successToast('Variable saved');
    } catch (error) {
      AlertsModel.errorToast("Error saving variable");
      warn("Error saving variable", error);
    }
  }

  static async getIsLocationSlugUnique(currentBrandSlug: string, currentLocationId: number, newLocationSlug?: string, newLocationShortLink?: string) {
    return await API.Spot.isLocationSlugUnique(Member.orgId, currentBrandSlug, currentLocationId, newLocationSlug, newLocationShortLink);
  }

  static deleteVariable(variable: LocationVarWithId) {
    let allVariables = { ...LocationVarsModel.state.allVariables };
    if (!allVariables || !allVariables[variable.id]) {
      warn("Variable not found");
      return;
    }
    delete allVariables[variable.id];
    LocationVarsModel.updateAllVariables(Object.values(allVariables as LocationVarInterface[]));
    API.LocationVars.deleteVariable(Member.orgId!, variable.id)
      .then(() => {
        // AlertsModel.successToast('Variable deleted');
      })
      .catch((error) => {
        AlertsModel.errorToast("Error deleting variable");
        warn("Error deleting variable", error);
      });
  }
}
